import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Check, ShieldCheck, TriangleAlert } from "lucide-react";
import "@/app/routes/dashboard/cockpit.css";
import { axiosInstance } from "@/lib/api";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import ClaudeLogo from "@/components/icons/ClaudeLogo";
import OpenAiLogo from "@/components/icons/OpenAiLogo";
import McpLogo from "@/components/icons/McpLogo";

type PendingRequest = {
  clientName: string;
  redirectHost: string;
  scopes: string[];
  expiresAt: number;
};

type Answer = { redirectUrl: string };

/** Claude and ChatGPT by where they send the person back; anything else by its own name. */
function knownApp(request: PendingRequest): { name: string; Logo: typeof ClaudeLogo } {
  const host = request.redirectHost.toLowerCase();
  if (host === "claude.ai" || host === "claude.com" || host.endsWith(".claude.ai")) return { name: "Claude", Logo: ClaudeLogo };
  if (host === "chatgpt.com" || host.endsWith(".chatgpt.com") || host.endsWith(".openai.com")) return { name: "ChatGPT", Logo: OpenAiLogo };
  return { name: request.clientName, Logo: McpLogo };
}

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string } } } | null)?.response?.data;
  return data?.error ?? "Creo could not read this request. Start again from the app you are connecting.";
}

/**
 * Where an MCP connector (Claude, ChatGPT, …) sends the person to sign in. They
 * are already signed in to Creo (ProtectedLayout), so this only asks: allow the
 * app to act as you? Allow or Cancel answers the backend, and the browser
 * follows the URL it returns back to the app. Follows the system theme, as
 * there is no cockpit here to pick one.
 */
export default function AuthorizeConnectorPage() {
  const theme = useMediaQuery("(prefers-color-scheme: dark)") ? "dark" : "light";
  const [params] = useSearchParams();
  const requestId = params.get("request") ?? "";
  const { user, logout } = useCurrentUser();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const request = useQuery({
    queryKey: ["mcpOAuthRequest", requestId],
    queryFn: async () => (await axiosInstance.get<PendingRequest>(`/mcp-oauth/requests/${requestId}`)).data,
    enabled: !!requestId,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Not optimistic: where the browser goes next only exists once the server answers.
  const answer = useMutation({
    mutationFn: async (choice: "approve" | "deny") =>
      (await axiosInstance.post<Answer>(`/mcp-oauth/requests/${requestId}/${choice}`)).data,
    onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
  });

  const switchAccount = () => {
    logout();
    navigate(`/login?redirect=${encodeURIComponent(pathname + search)}`, { replace: true });
  };

  const app = request.data ? knownApp(request.data) : null;
  const failed = !requestId || request.isError || answer.isError;
  const pending = answer.isPending || answer.isSuccess;

  return (
    <div className={`co-shell co-page${theme === "dark" ? " dark" : ""} bg-co-ground text-co-text`}>
      <main className="flex min-h-dvh w-full items-center justify-center px-4 py-10">
        <div className="co-glass-chrome mx-auto flex w-full min-w-0 max-w-[420px] flex-col items-center gap-5 rounded-panel px-6 py-8 text-center">
          {failed ? (
            <>
              <span className="grid size-14 place-items-center rounded-full border border-co-line2 bg-co-surface-solid">
                <TriangleAlert className="size-6 text-co-faint" strokeWidth={1.6} aria-hidden />
              </span>
              <div className="flex w-full min-w-0 flex-col gap-2">
                <h1 className="font-section text-[20px] font-bold leading-[1.15] tracking-normal text-balance text-co-text">
                  This request can't be used
                </h1>
                <p className="text-[13.5px] leading-[1.55] tracking-normal text-co-muted">
                  {requestId
                    ? errorMessage(request.error ?? answer.error)
                    : "The link is missing its request. Start again from the app you are connecting."}
                </p>
              </div>
              <KitButton variant="secondary" onClick={() => navigate("/dashboard")}>
                Go to Creo
              </KitButton>
            </>
          ) : !app ? (
            <div className="flex w-full flex-col items-center gap-4" aria-busy aria-label="Loading the request">
              <span className="size-14 animate-pulse rounded-full bg-co-tonal" />
              <span className="h-5 w-[220px] animate-pulse rounded-[6px] bg-co-tonal" />
              <span className="h-3.5 w-[280px] max-w-full animate-pulse rounded-[5px] bg-co-tonal" />
              <span className="h-3.5 w-[200px] animate-pulse rounded-[5px] bg-co-tonal" />
              <span className="mt-2 h-11 w-full animate-pulse rounded-pill bg-co-tonal" />
            </div>
          ) : (
            <>
              <span className="grid size-14 place-items-center rounded-full border border-co-line2 bg-co-surface-solid">
                <app.Logo className="size-6" />
              </span>
              <div className="flex w-full min-w-0 flex-col gap-2">
                <h1 className="font-section text-[20px] font-bold leading-[1.15] tracking-normal text-balance text-co-text">
                  Allow {app.name} to use Creo?
                </h1>
                <p className="text-[13.5px] leading-[1.55] tracking-normal text-co-muted">
                  {app.name} will act as you, with your role in each workspace. It never sees your password.
                </p>
              </div>
              <ul className="m-0 flex w-full list-none flex-col gap-2 p-0 text-start">
                {[
                  "Read and work on your brands, as you can",
                  "Stay connected until you remove it",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5 text-[13px] text-co-text">
                    <Check className="mt-0.5 size-3.5 shrink-0 text-co-accent" aria-hidden />
                    {line}
                  </li>
                ))}
              </ul>
              {user?.email ? (
                <p className="text-[12.5px] text-co-muted">
                  Signed in as <span className="font-medium text-co-text">{user.email}</span>
                  {" · "}
                  <button
                    type="button"
                    onClick={switchAccount}
                    disabled={pending}
                    className="font-medium text-co-accent hover:text-co-accent-ink disabled:opacity-40"
                  >
                    Not you?
                  </button>
                </p>
              ) : null}
              <div className="flex w-full flex-col gap-2">
                <KitButton
                  size="lg"
                  iconLeft={ShieldCheck}
                  loading={pending && answer.variables === "approve"}
                  isDisabled={pending}
                  onClick={() => answer.mutate("approve")}
                >
                  Allow
                </KitButton>
                <KitButton
                  size="lg"
                  variant="secondary"
                  loading={pending && answer.variables === "deny"}
                  isDisabled={pending}
                  onClick={() => answer.mutate("deny")}
                >
                  Cancel
                </KitButton>
              </div>
              <p className="text-[12px] leading-relaxed text-co-faint">
                You'll go back to {request.data?.redirectHost} after you choose.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
